import { Eye, EyeSlash, Trash, CopySimple } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import { Button } from '../ui/button';

export default function BatchPropertiesPanel() {
  const { selectedLayerIds, layers, removeLayer, toggleLayerVisibility, duplicateLayer, pushUndo, selectLayer } =
    useSceneStore();

  const selectedLayers = layers.filter((l) => selectedLayerIds.includes(l.id));
  const count = selectedLayers.length;
  if (count < 2) return null;

  const allVisible = selectedLayers.every((l) => l.visible);
  const allHidden = selectedLayers.every((l) => !l.visible);

  function handleToggleVisibility() {
    // If any are visible, hide all. If all hidden, show all.
    const targetVisible = allHidden;
    const mgr = getSpineManager();
    selectedLayers.forEach((l) => {
      if (l.visible !== targetVisible) {
        toggleLayerVisibility(l.id);
        mgr.setLayerVisible(l.id, targetVisible);
      }
    });
  }

  function handleDeleteAll() {
    pushUndo();
    // Delete in order to avoid index shifting issues
    selectedLayerIds.forEach((id) => removeLayer(id));
    selectLayer(null);
  }

  function handleDuplicateAll() {
    pushUndo();
    selectedLayers.forEach((l) => duplicateLayer(l.id));
  }

  const visibilityLabel = allHidden ? 'Show all' : allVisible ? 'Hide all' : 'Toggle visibility';

  return (
    <div className="p-4 space-y-4" data-testid="batch-properties-panel">
      {/* Selection summary */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-accent-muted border border-accent/30 rounded-lg">
        <span className="font-mono text-xs font-semibold text-accent">{count}</span>
        <span className="font-sans text-xs text-text-secondary">layers selected</span>
        <button
          onClick={() => selectLayer(selectedLayerIds[selectedLayerIds.length - 1] ?? null)}
          className="ml-auto text-[10px] font-mono text-text-tertiary hover:text-text-primary transition-colors"
        >
          collapse
        </button>
      </div>

      {/* Layer names list */}
      <div className="space-y-0.5">
        {selectedLayers.map((l) => (
          <div key={l.id} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-bg-hover">
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: l.labelColor ?? '#6366f1' }}
            />
            <span className="text-xs font-sans text-text-secondary truncate flex-1">{l.name}</span>
          </div>
        ))}
      </div>

      {/* Batch actions */}
      <div className="space-y-2">
        <span className="block text-[10px] font-mono font-semibold text-text-tertiary uppercase tracking-wide">
          Batch Actions
        </span>

        <button
          onClick={handleToggleVisibility}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-border transition-all duration-fast"
        >
          {allHidden
            ? <Eye size={13} className="text-text-tertiary" />
            : <EyeSlash size={13} className="text-text-tertiary" />}
          {visibilityLabel}
        </button>

        <div className="flex gap-2">
          <Button
            variant="violet"
            size="sm"
            className="flex-1 gap-1.5"
            onClick={handleDuplicateAll}
          >
            <CopySimple size={13} />
            Duplicate all
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="flex-1 gap-1.5"
            onClick={handleDeleteAll}
          >
            <Trash size={13} />
            Delete all
          </Button>
        </div>
      </div>
    </div>
  );
}
